/**
 * Asset Search — full search page with cascading filters + results grid.
 * Route: /assets
 *
 * Ports FAIM I's asset list/search behaviour:
 *  - Free-text term (asset code / serial / equipment number / model name).
 *  - Cascading geography filters: Area → Country → Business Line → Region.
 *    Region only lists options once both a country and a business line are chosen.
 *  - Brand → Model (global master data, model narrowed by brand).
 *  - "Show archived" toggle (archived = Returned Lease/Rental, Scrap, Sold).
 *
 * State lives entirely in the URL search params so filters are shareable and
 * survive refresh. Filter-option lists are user-scoped on the server and refetch
 * as the parent selection changes.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import { graphql } from '../../gql';
import { DataGrid } from '../../components/data-grid';
import { TextInput, SelectInput, Checkbox, PageHeader, LoadingOverlay } from '../../components/ui';
import type { SelectOption } from '../../components/ui';
import type { ColDef, RowClickedEvent } from 'ag-grid-community';

const ASSET_SEARCH_FILTER_OPTIONS_QUERY = graphql(`
  query AssetSearchFilterOptions(
    $areaId: Int
    $countryId: Int
    $businessLineId: Int
    $brandId: Int
  ) {
    assetSearchFilterOptions(
      areaId: $areaId
      countryId: $countryId
      businessLineId: $businessLineId
      brandId: $brandId
    ) {
      areas { id name }
      countries { id name }
      businessLines { id name }
      regions { id name }
      brands { id name }
      models { id name }
    }
  }
`);

const SEARCH_ASSETS_QUERY = graphql(`
  query SearchAssets($filter: AssetSearchFilterInput) {
    searchAssets(filter: $filter) {
      id
      assetCode
      equipmentNumber
      sapOrderNumber
      serialNumber
      plantId
      plantName
      businessLineName
      regionName
      clusterName
      brandName
      modelName
      statusLocalizationKey
      isArchived
    }
  }
`);

const DEBOUNCE_MS = 300;

interface AssetRow {
  id: number;
  assetCode: string;
  equipmentNumber?: string | null;
  sapOrderNumber?: string | null;
  serialNumber?: string | null;
  plantId: number;
  plantName: string;
  businessLineName: string;
  regionName: string;
  clusterName: string;
  brandName: string;
  modelName: string;
  statusLocalizationKey: string;
  isArchived: boolean;
}

/** Parse a search param into a positive int id, or undefined when absent/invalid. */
function paramId(params: URLSearchParams, key: string): number | undefined {
  const raw = params.get(key);
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/** Build SelectInput options with a leading "All" entry. */
function withAll(label: string, options: ReadonlyArray<{ id: number; name: string }>): SelectOption[] {
  return [{ value: '', label }, ...options.map((o) => ({ value: o.id, label: o.name }))];
}

export function AssetSearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive the typed filter selections from the URL (single source of truth).
  const areaId = paramId(searchParams, 'area');
  const countryId = paramId(searchParams, 'country');
  const businessLineId = paramId(searchParams, 'bl');
  const regionId = paramId(searchParams, 'region');
  const brandId = paramId(searchParams, 'brand');
  const modelId = paramId(searchParams, 'model');
  const includeArchived = searchParams.get('archived') === 'true';
  // Quick-search bar navigates here with `?input=`; treat it as the initial term.
  const term = searchParams.get('q') ?? searchParams.get('input') ?? '';

  // Local, debounced mirror of the free-text term so typing doesn't thrash the URL.
  const [termInput, setTermInput] = useState(term);

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('input');
          const trimmed = termInput.trim();
          if (trimmed) next.set('q', trimmed);
          else next.delete('q');
          return next;
        },
        { replace: true },
      );
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // setSearchParams is stable; only re-run when the typed term changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termInput]);

  // Cascading filter-option lists, scoped to the user's accessible plants and
  // narrowed by the current parent selections.
  const { data: optionsData, loading: optionsLoading } = useQuery(ASSET_SEARCH_FILTER_OPTIONS_QUERY, {
    variables: {
      areaId: areaId ?? null,
      countryId: countryId ?? null,
      businessLineId: businessLineId ?? null,
      brandId: brandId ?? null,
    },
  });

  const options = optionsData?.assetSearchFilterOptions;

  const filter = useMemo(
    () => ({
      areaId: areaId ?? null,
      countryId: countryId ?? null,
      businessLineId: businessLineId ?? null,
      regionId: regionId ?? null,
      brandId: brandId ?? null,
      modelId: modelId ?? null,
      searchTerm: term.trim() || null,
      includeArchived,
    }),
    [areaId, countryId, businessLineId, regionId, brandId, modelId, term, includeArchived],
  );

  const { data: searchData, loading: searchLoading, error } = useQuery(SEARCH_ASSETS_QUERY, {
    variables: { filter },
  });

  const assets = (searchData?.searchAssets ?? []) as AssetRow[];

  /**
   * Update one filter param and clear the descendants that no longer apply,
   * so a stale child selection can never survive a parent change.
   */
  const updateFilter = useCallback(
    (key: string, value: string, clearChildren: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        clearChildren.forEach((child) => next.delete(child));
        return next;
      });
    },
    [setSearchParams],
  );

  const handleClear = useCallback(() => {
    setTermInput('');
    setSearchParams({});
  }, [setSearchParams]);

  const columnDefs = useMemo<ColDef<AssetRow>[]>(
    () => [
      { field: 'businessLineName', headerName: 'Business Line', flex: 1 },
      { field: 'regionName', headerName: 'Region', flex: 1 },
      { field: 'plantName', headerName: 'Plant', flex: 1 },
      { field: 'assetCode', headerName: 'Asset Code', flex: 1 },
      { field: 'equipmentNumber', headerName: 'Equipment No.', flex: 1 },
      { field: 'sapOrderNumber', headerName: 'SAP Order No.', flex: 1 },
      { field: 'serialNumber', headerName: 'Serial No.', flex: 1 },
      { field: 'clusterName', headerName: 'Cluster', flex: 1 },
      { field: 'brandName', headerName: 'Brand', flex: 1 },
      { field: 'modelName', headerName: 'Model', flex: 1 },
      { field: 'statusLocalizationKey', headerName: 'Status', flex: 1 },
    ],
    [],
  );

  const onRowClicked = useCallback(
    (event: RowClickedEvent<AssetRow>) => {
      if (event.data) navigate(`/assets/${event.data.id}`);
    },
    [navigate],
  );

  return (
    <>
      <LoadingOverlay visible={searchLoading || optionsLoading} />

      <main className="max-w-[1440px] mx-auto px-6 py-8">
        <PageHeader title="Asset Search" />

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <TextInput
            search
            value={termInput}
            onChange={(e) => setTermInput(e.target.value)}
            placeholder="Search by asset code, serial, equipment no., model..."
          />
          <SelectInput
            label="Area"
            fullWidth={false}
            sx={{ minWidth: 160 }}
            value={areaId ? String(areaId) : ''}
            onChange={(e) => updateFilter('area', String(e.target.value), ['country', 'bl', 'region'])}
            options={withAll('All Areas', options?.areas ?? [])}
          />
          <SelectInput
            label="Country"
            fullWidth={false}
            sx={{ minWidth: 160 }}
            value={countryId ? String(countryId) : ''}
            onChange={(e) => updateFilter('country', String(e.target.value), ['bl', 'region'])}
            options={withAll('All Countries', options?.countries ?? [])}
          />
          <SelectInput
            label="Business Line"
            fullWidth={false}
            sx={{ minWidth: 160 }}
            value={businessLineId ? String(businessLineId) : ''}
            onChange={(e) => updateFilter('bl', String(e.target.value), ['region'])}
            options={withAll('All Business Lines', options?.businessLines ?? [])}
          />
          <SelectInput
            label="Region"
            fullWidth={false}
            sx={{ minWidth: 160 }}
            value={regionId ? String(regionId) : ''}
            onChange={(e) => updateFilter('region', String(e.target.value), [])}
            options={withAll('All Regions', options?.regions ?? [])}
          />
          <SelectInput
            label="Brand"
            fullWidth={false}
            sx={{ minWidth: 160 }}
            value={brandId ? String(brandId) : ''}
            onChange={(e) => updateFilter('brand', String(e.target.value), ['model'])}
            options={withAll('All Brands', options?.brands ?? [])}
          />
          <SelectInput
            label="Model"
            fullWidth={false}
            sx={{ minWidth: 160 }}
            value={modelId ? String(modelId) : ''}
            onChange={(e) => updateFilter('model', String(e.target.value), [])}
            options={withAll('All Models', options?.models ?? [])}
          />
          <Checkbox
            label="Show archived"
            checked={includeArchived}
            onChange={(checked) => updateFilter('archived', checked ? 'true' : '', [])}
          />
          <Button variant="outlined" onClick={handleClear}>
            Clear
          </Button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-negative-faded-20 border border-negative rounded text-sm text-negative">
            {error.message}
          </div>
        )}

        {/* Results grid */}
        {assets.length > 0 && (
          <DataGrid<AssetRow>
            rowData={assets}
            columnDefs={columnDefs}
            onRowClicked={onRowClicked}
            height="75vh"
          />
        )}

        {/* Empty state */}
        {searchData && assets.length === 0 && (
          <div className="text-center py-12 text-foreground-faded">
            No assets match the current filters.
          </div>
        )}
      </main>
    </>
  );
}
